import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { firestore } from "../firebase";
import { mapSnapshotToUser } from "../lib/map-snapshot-to-user";
import { User } from "../types/User";
import { debugLogError } from "../utils/debug-log-error";

export function useUser(userUuid: string | null | undefined) {
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userUuid) {
      setUser(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const userDocRef = doc(firestore, "users", userUuid);

    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        setInitialized(true);

        if (docSnapshot.exists()) {
          const data = docSnapshot.data();

          const updatedFields: Partial<User> = {};

          // Check for backwards compatibility:
          if (!data.createdAt) {
            updatedFields.createdAt = Date.now();
          }

          setUser(mapSnapshotToUser(userUuid, data));

          if (Object.keys(updatedFields).length > 0) {
            updateDoc(userDocRef, {
              ...updatedFields,
            });
          }
        } else {
          setUser(null);
          setError("User profile not found.");
        }
        setLoading(false);
      },
      (error) => {
        debugLogError("useUserProfile::onSnapshot", error);
        setError("Failed to fetch profile.");
        setUser(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userUuid]);

  return useMemo(
    () => ({
      user,
      loading: (loading && !!userUuid) || !initialized,
      error,
    }),
    [user, loading, error, userUuid, initialized]
  );
}
